import React, { useState, useEffect } from "react";
import {
  Grid,
  makeStyles,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { useTranslation } from 'react-i18next';
import GeneralModal from "./GeneralModal";
import { TextFieldInput } from "../components-atoms";
import { ButtonTextPrimary, ButtonPrimary } from "../components-atoms";
import { editTkProUser, EditProUserData } from "../redux/actions/subscriptions";
import { ProOrganisationMember } from "../redux/actions/subscriptions";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  member: ProOrganisationMember | null;
  proTeamId: string;
}

export const EditProUserModal = ({
  visible,
  onClose,
  onSuccess,
  member,
  proTeamId
}: IProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<EditProUserData>({
    Email: "",
    FirstName: "",
    LastName: "",
    IsManager: false,
    ApprovalThreshold: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (member) {
      setFormData({
        Email: member.Email || "",
        FirstName: member.FirstName || "",
        LastName: member.LastName || "",
        IsManager: member.RoleName === "Team Manager",
        ApprovalThreshold: member.ApprovalThreshold || 0,
      });
    }
  }, [member]);

  const handleInputChange = (field: keyof EditProUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'number' 
      ? parseFloat((event.target as HTMLInputElement).value) || 0
      : event.target.type === 'checkbox' 
        ? (event.target as HTMLInputElement).checked
        : (event.target as HTMLInputElement).value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const onFormSubmit = () => {
    if (!isFormValid() || !member) {
      return;
    }

    setLoading(true);
    setError(null);

    const requestData = {
      ...formData
    };
    
    editTkProUser(
      proTeamId,
      member.CustomerId,
      requestData,
      (response) => {
        setLoading(false);
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      },
      (err) => {
        setLoading(false);
        
        // Handle different error structures
        let errorMessage = 'Failed to update user. Please try again.';
        
        if (typeof err === 'string') {
          errorMessage = err;
        } else if (err?.response?.data?.Error) {
          errorMessage = err.response.data.Error;
        } else if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      }
    );
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      Email: "",
      FirstName: "",
      LastName: "",
      IsManager: false,
      ApprovalThreshold: 0,
    });
    setError(null);
    setLoading(false);
    onClose();
  };

  const isFormValid = () => {
    return formData.FirstName.trim() && 
           formData.LastName.trim() && 
           formData.Email.trim() &&
           /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email);
  };

  return (
    <GeneralModal
      title="Edit Pro User"
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextFieldInput
                  label="First Name"
                  value={formData.FirstName}
                  onChange={handleInputChange('FirstName')}
                  placeholder="Enter first name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
                  label="Last Name"
                  value={formData.LastName}
                  onChange={handleInputChange('LastName')}
                  placeholder="Enter last name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Email Address"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange('Email')}
                  placeholder="Enter email address"
                  required
                  disabled={loading}
                  error={!!error}
                  helperText={error}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.IsManager}
                      onChange={handleInputChange('IsManager')}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Is Manager"
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
label={`Approval Threshold (${t('currencySymbol')})`}
                  type="number"
                  value={formData.ApprovalThreshold / 100}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setFormData(prev => ({
                      ...prev,
                      ApprovalThreshold: Math.round(value * 100)
                    }));
                    if (error) {
                      setError(null);
                    }
                  }}
                  placeholder="0.00"
                  disabled={loading}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <ButtonTextPrimary onClick={handleClose} disabled={loading}>
            Cancel
          </ButtonTextPrimary>
          <ButtonPrimary
            onClick={onFormSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "Updating..." : "Update User"}
          </ButtonPrimary>
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles({
  fieldset: {
    border: "none",
    padding: 0,
    margin: 0,
  },
});

export default EditProUserModal;
